/**
 * The scene as a reflection probe capture draws it (`renderer_scene_cull.cpp:3708-3711`): only the
 * instances on the probe's cull mask, and no environment for an interior probe
 * (`render_forward_clustered.cpp:1819-1821`), which clears to the default clear colour instead.
 */
import { isAmbientLight } from '../ambientLight.js';
import { hideObjects, isEditorHelper } from '../editorHelpers.js';
import { isInMask } from '../visualLayers.js';
/**
 * Stages `scene` for a capture of `probe`, and answers the call that puts it back. An interior
 * capture clears to `clearColor`, the project's default clear colour.
 */
export function stageCapture(scene, probe, clearColor) {
    const restores = [hideCulled(scene, probe.cullMask)];
    if (probe.interior)
        restores.push(dropEnvironment(scene, clearColor));
    return () => {
        for (const restore of restores.reverse())
            restore();
    };
}
/** Whether `object` is an instance Godot culls by its layers: a drawn surface or a light. */
function isInstance(object) {
    const o = object;
    return !!(o.isMesh ||
        o.isLine ||
        o.isPoints ||
        o.isSprite ||
        o.isDirectionalLight ||
        o.isPointLight ||
        o.isSpotLight ||
        o.isRectAreaLight);
}
/** Hides the instances outside `cullMask`, and the editor chrome, which no scene mask holds. */
function hideCulled(scene, cullMask) {
    return hideObjects(scene, (object) => isEditorHelper(object) || (isInstance(object) && !isInMask(object, cullMask)));
}
function dropEnvironment(scene, clearColor) {
    const { environmentIntensity, background } = scene;
    const ambients = [];
    scene.traverse((object) => {
        if (isAmbientLight(object))
            ambients.push({ light: object, intensity: object.intensity });
    });
    // The intensity, not `environment`: a program compiles `USE_ENVMAP` from the environment.
    scene.environmentIntensity = 0;
    scene.background = clearColor;
    // Zero, not a scale by zero: an ambient of `inf` energy scales to NaN.
    for (const { light } of ambients)
        light.intensity = 0;
    return () => {
        scene.environmentIntensity = environmentIntensity;
        scene.background = background;
        for (const { light, intensity } of ambients)
            light.intensity = intensity;
    };
}
//# sourceMappingURL=captureStage.js.map